import React from 'react';
import { Typography, Box, Paper, Stack } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { tokens } from '../../theme';
import { PageHeader, StatTile, StatusPill } from '../../components/ui';
import { formatNumber } from '../../utils/format';

const MARKETING_MODULES = [
  {
    id: 'import',
    title: 'Zaimportuj i zaproś klientów',
    description: 'Zachęć klientów do rezerwowania wizyt online 24/7',
    icon: <PeopleOutlineIcon />,
    status: 'Nie zaproszono',
  },
  {
    id: 'social',
    title: 'Marketing w mediach społecznościowych',
    description: 'Twórz i udostępniaj własne posty i grafiki',
    icon: <ShareIcon />,
    status: 'Nieaktywny',
  },
  {
    id: 'sms-email',
    title: 'Kampanie e-mail i SMS',
    description: 'Włącz automatyczne kampanie lub wysyłaj jednorazowe wiadomości',
    icon: <MailOutlineIcon />,
    status: 'Nieaktywny',
  },
  {
    id: 'promotions',
    title: 'Promocje',
    description: 'Włącz promocje, aby zachęcić klientów do wizyt',
    icon: <LocalOfferIcon />,
    status: 'Nieaktywny',
  },
];

// Wartosci pokazowe — modul kampanii nie ma jeszcze wlasnych danych.
const PREVIEW_STATS: { label: string; value: number; unit?: string; change?: number; hint: string }[] = [
  { label: 'Wysłane SMS-y', value: 342, change: 15, hint: 'w tym miesiącu' },
  { label: 'Wysłane e-maile', value: 1204, change: 8, hint: 'w tym miesiącu' },
  { label: 'Nowi klienci z kampanii', value: 23, change: 12, hint: 'w tym miesiącu' },
  { label: 'Konwersja kampanii', value: 4.2, unit: '%', hint: '+0,5 pp w tym miesiącu' },
];

export const SalonMarketingPage: React.FC = () => {
  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Marketing' }]}
        title="Kampanie"
        description="Przyciągaj nowych klientów i utrzymuj tych, których już masz."
      />

      <Box>
        <Typography component="h2" variant="h4">Narzędzia marketingowe</Typography>
        <Typography variant="body2" sx={{ color: tokens.muted, mt: 0.5, mb: 2 }}>
          Te moduły są w przygotowaniu — udostępnimy je w kolejnych aktualizacjach.
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
          {MARKETING_MODULES.map((mod) => (
            <Paper key={mod.id} sx={{ p: { xs: 2, md: 2.5 }, height: '100%' }}>
              <Stack direction="row" alignItems="flex-start" gap={2}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    bgcolor: tokens.plumSoft,
                    color: tokens.plum,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    '& .MuiSvgIcon-root': { fontSize: 22 },
                  }}
                >
                  {mod.icon}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.0625rem', lineHeight: 1.35 }}>
                    {mod.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: tokens.inkSoft, mt: 0.5, lineHeight: 1.5 }}>
                    {mod.description}
                  </Typography>
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1.5 }}>
                    <StatusPill label="Wkrótce" tone="plum" size="small" noDot />
                    <Typography variant="caption" sx={{ color: tokens.muted }}>Stan: {mod.status.toLowerCase()}</Typography>
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Box>
      </Box>

      <Box sx={{ mt: { xs: 3.5, md: 4.5 } }}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
          <Typography component="h2" variant="h4">Przegląd kampanii</Typography>
          <StatusPill label="Dane przykładowe" tone="neutral" size="small" noDot />
        </Stack>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
          {PREVIEW_STATS.map((s) => (
            <StatTile
              key={s.label}
              label={s.label}
              value={formatNumber(s.value)}
              unit={s.unit}
              change={s.change}
              changeLabel={s.change !== undefined ? s.hint : undefined}
              hint={s.hint}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};
